import { useEffect, useMemo, useState } from "react";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import { ClockConfigValue } from "../../../api/clockConfig";

type Row = { rowId: number; key: string; value: string };

export interface ClockConfigFormProps {
  /** Values currently stored on the document being edited. */
  values: Record<string, ClockConfigValue>;
  /**
   * What a key falls back to once it is removed here — the global document's values, on
   * the override form. Shown per row as a hint. Empty on the global form: there the
   * fallback is a default compiled into the clock, and only the clock knows it.
   */
  inheritedValues?: Record<string, ClockConfigValue>;
  /** How the fallback is described, e.g. "Global". */
  inheritLabel?: string;
  /**
   * When set, keys are picked from this list instead of typed freely, and anything else
   * is rejected. The override form passes the global document's keys: a clock can only
   * deviate from a value the fleet actually has. Leave undefined on the global form,
   * where new keys are created.
   */
  allowedKeys?: string[];
  saving?: boolean;
  error?: string | null;
  saveLabel?: string;
  onSave: (values: Record<string, ClockConfigValue>) => void;
  onCancel?: () => void;
}

/** Numeric text becomes a number, `true`/`false` a boolean; anything else stays a string. */
const parseValue = (raw: string): ClockConfigValue => {
  const trimmed = raw.trim();
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  const parsed = Number(trimmed);
  return trimmed !== "" && Number.isFinite(parsed) ? parsed : trimmed;
};

/**
 * The editor for one clock-configuration document, shared by the global tab, the
 * per-clock override dialog and the device modal.
 *
 * Deliberately a plain key/value table with no list of known keys of its own. The clock
 * owns every default and safe range — it has to, since it must behave correctly offline
 * and on builds no server knows about — and it clamps every value it recognises. A list
 * here would be a second copy that drifts, and would mean a deploy before a key a clock
 * release already reads could be set.
 *
 * Removing a row removes the key, which is the rollback path: the clock falls back to the
 * global value, or to the default compiled into its build.
 */
const ClockConfigForm = ({
  values,
  inheritedValues = {},
  inheritLabel = "Inherited",
  allowedKeys,
  saving = false,
  error = null,
  saveLabel = "Save",
  onSave,
  onCancel,
}: ClockConfigFormProps) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [nextRowId, setNextRowId] = useState(0);

  // Keyed on content, not identity: a parent rendering `values={{...}}` inline would
  // otherwise hand this effect a new object every render and loop through setState.
  const valuesKey = JSON.stringify(values);

  // Re-seed whenever the document being edited changes (a different clock, or a fresh
  // copy after a save).
  useEffect(() => {
    const seeded = Object.keys(values)
      .sort()
      .map((key, index) => ({
        rowId: index,
        key,
        value: String(values[key]),
      }));

    setRows(seeded);
    setNextRowId(seeded.length);
  }, [valuesKey]);

  const rowError = useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      const key = row.key.trim();
      if (key === "") return "A key cannot be empty";
      if (seen.has(key)) return `"${key}" is listed twice`;
      seen.add(key);
      if (allowedKeys && !allowedKeys.includes(key)) {
        return `"${key}" is not in the global configuration — set it there first`;
      }
      if (row.value.trim() === "") return `"${key}" needs a value`;
    }
    return null;
  }, [rows, allowedKeys]);

  // Keys still available to pick, so the same key cannot be added twice.
  const unusedKeys = useMemo(() => {
    if (!allowedKeys) return [];
    const taken = new Set(rows.map((row) => row.key.trim()));
    return allowedKeys.filter((key) => !taken.has(key));
  }, [allowedKeys, rows]);

  const canAddRow = !allowedKeys || unusedKeys.length > 0;

  const handleSave = () => {
    const next: Record<string, ClockConfigValue> = {};
    rows.forEach((row) => {
      next[row.key.trim()] = parseValue(row.value);
    });
    onSave(next);
  };

  const updateRow = (rowId: number, patch: Partial<Row>) =>
    setRows((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)),
    );

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {rows.length === 0 && (
        <DSTypography variant="body2" color="text.secondary">
          {allowedKeys && allowedKeys.length === 0
            ? "The global configuration is empty, so there is nothing to override yet."
            : "No values set — clocks run on the defaults compiled into their build."}
        </DSTypography>
      )}

      {rows.map((row) => {
        const inherited = inheritedValues[row.key.trim()];

        return (
          <DSBox
            key={row.rowId}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {allowedKeys ? (
              <DSAutocomplete<string>
                size="small"
                disabled={saving}
                options={
                  // Keep the row's own key in its options, or MUI treats it as invalid.
                  row.key ? [row.key, ...unusedKeys] : unusedKeys
                }
                value={row.key || null}
                onChange={(_, value) =>
                  updateRow(row.rowId, { key: value ?? "" })
                }
                sx={{ flex: 2, minWidth: 220 }}
                renderInput={(params) => (
                  <DSTextField {...params} label="Key" size="small" />
                )}
              />
            ) : (
              <DSTextField
                label="Key"
                size="small"
                disabled={saving}
                value={row.key}
                placeholder="e.g. eco.idleDays"
                onChange={(e) => updateRow(row.rowId, { key: e.target.value })}
                sx={{ flex: 2, minWidth: 220 }}
              />
            )}
            <DSTextField
              label="Value"
              size="small"
              disabled={saving}
              value={row.value}
              helperText={
                inherited !== undefined
                  ? `${inheritLabel} ${inherited}`
                  : undefined
              }
              onChange={(e) => updateRow(row.rowId, { value: e.target.value })}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <DSButton
              size="small"
              color="error"
              disabled={saving}
              sx={{ mt: 0.5 }}
              onClick={() =>
                setRows((prev) =>
                  prev.filter((entry) => entry.rowId !== row.rowId),
                )
              }
            >
              Remove
            </DSButton>
          </DSBox>
        );
      })}

      {rowError && (
        <DSTypography variant="caption" color="error">
          {rowError}
        </DSTypography>
      )}

      <DSBox>
        <DSButton
          size="small"
          variant="outlined"
          disabled={saving || !canAddRow}
          onClick={() => {
            setRows((prev) => [
              ...prev,
              { rowId: nextRowId, key: "", value: "" },
            ]);
            setNextRowId((id) => id + 1);
          }}
        >
          Add key
        </DSButton>
        {allowedKeys && !canAddRow && rows.length > 0 && (
          <DSTypography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1.5 }}
          >
            Every global key is already overridden here.
          </DSTypography>
        )}
      </DSBox>

      {error && <DSTypography color="error">{error}</DSTypography>}

      <DSBox sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {onCancel && (
          <DSButton size="small" onClick={onCancel} disabled={saving}>
            Cancel
          </DSButton>
        )}
        <DSButton
          variant="contained"
          size="small"
          disabled={saving || !!rowError}
          onClick={handleSave}
        >
          {saveLabel}
        </DSButton>
      </DSBox>
    </DSBox>
  );
};

export default ClockConfigForm;
